// Step 2's library: the photos and resumes the actor already has in the PM API (docs/14,
// "The picker"). A new account has none until its first upload; a returning actor picks from
// theirs instead of uploading. Nothing in the library is ever replaced or deleted from the flow.

import { IMediaAssetDto } from "../../../shared/api/submission/types";
import { getSubmissionWebSessionKey } from "../../../helpers/submissionAuth";

export interface IMediaLibrary {
  photos: IMediaAssetDto[];
  resumes: IMediaAssetDto[];
}

export const EMPTY_LIBRARY: IMediaLibrary = { photos: [], resumes: [] };

// The photo a returning actor starts with: their main headshot, so Continue works at once.
// Only when nothing is chosen yet, neither in this visit nor on the draft.
export const preselectedPhoto = (
  library: IMediaLibrary,
  current: IMediaAssetDto | null | undefined
): IMediaAssetDto | null => {
  if (current) return null;
  return library.photos.filter((p) => p.isMain)[0] || null;
};

// The resume a returning actor starts with: their main one (profileManagement.listResumes), so
// "Show casting who you are" is done for most members. Only when this submission has no choice
// yet: no resume, and no note either (a note alone is a choice).
export const preselectedResume = (
  library: IMediaLibrary,
  current: IMediaAssetDto | null | undefined,
  note: string | null | undefined
): IMediaAssetDto | null => {
  if (current || (note || "").trim()) return null;
  return library.resumes.filter((r) => r.isMain)[0] || null;
};

// An upload (or a pick) joins the library at once, in slot order, replacing an older copy of
// the same slot (a replaced photo keeps its slot, with a new URL).
export const withAsset = (list: IMediaAssetDto[], asset: IMediaAssetDto | null | undefined): IMediaAssetDto[] => {
  if (!asset) return list;
  const rest = list.filter((a) => a.assetId !== asset.assetId);
  return [...rest, asset].sort((a, b) => a.assetId - b.assetId);
};

// "Go Pro" at the photo limit: the membership site, signed in with the session key the flow's
// token carries. A new tab, so the submission stays where it is.
export const proUpgradeUrl = (): string => {
  const base = (process.env.REACT_APP_CWB_Membership_Base || "").replace(/\/+$/, "");
  const key = getSubmissionWebSessionKey();
  return key ? `${base}/promo?k=${encodeURIComponent(key)}` : `${base}/promo`;
};
